/**
 * Rotates an image file 90 degrees clockwise
 * @param file - The image file to rotate
 * @returns Promise<File> - The rotated image as a new File object
 */
export async function rotateImageFile(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const image = new Image();

    if (!ctx) {
      reject(new Error("Could not get canvas context"));
      return;
    }

    // Create object URL from the file
    const url = URL.createObjectURL(file);

    image.onload = () => {
      // Swap width and height for 90-degree rotation
      canvas.width = image.height;
      canvas.height = image.width;

      // Rotate the image 90 degrees clockwise
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((90 * Math.PI) / 180);
      ctx.drawImage(image, -image.width / 2, -image.height / 2);

      // Get the original file format
      const originalFormat = file.type.split("/")[1] || "jpeg";

      canvas.toBlob(
        (blob) => {
          // Clean up the object URL
          URL.revokeObjectURL(url);

          if (!blob) {
            reject(new Error("Failed to create blob from canvas"));
            return;
          }

          const rotatedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: file.lastModified,
          });

          resolve(rotatedFile);
        },
        `image/${originalFormat}`,
        0.92,
      );
    };

    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };

    image.src = url;
  });
}
